import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

import { semanticTokens } from './semantic-tokens';
import { Drawer } from './components/Drawer';
import { Input } from './components/Input';
import { Menu } from './components/Menu';
import { Modal } from './components/Modal';
import { Popover } from './components/Popover';
import { Select } from './components/Select';
import { Slider } from './components/Slider';
import { Switch } from './components/Switch';
import { Table } from './components/Table';
import { Textarea } from './components/Textarea';
import { Toast } from './components/Toast';
import { Tooltip } from './components/Tooltip';
import { Button } from './components/Button';

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

export const theme = extendTheme({
  config,
  semanticTokens,
  // Global typography and layout defaults to ensure consistent look & feel
  styles: {
    global: {
      ':root': {
        colorScheme: 'light',
        '--tg-glass-blur': '16px',
      },
      // Prevent iOS from auto-adjusting font sizes unexpectedly
      html: {
        WebkitTextSizeAdjust: '100%',
      },
      body: {
        bg: 'bg.canvas',
        color: 'text.primary',
        overscrollBehavior: 'contain', // Prevent scroll chaining on mobile
      },
      // Improve tap responsiveness and reduce 300ms delay/ghost tap issues on mobile
      'button, [role="button"]': {
        touchAction: 'manipulation',
      },
      '@media (prefers-reduced-transparency: reduce)': {
        ':root': {
          '--tg-glass-blur': '6px',
        },
      },
    },
  },
  // Standardized radii used across surfaces and controls
  radii: {
    none: '0',
    sm: '6px',
    md: '10px',
    lg: '14px',
    xl: '20px',
    '2xl': '24px',
    full: '9999px',
  },
  // Layering to guarantee correct overlay stacking order
  zIndices: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },
  // Type scale and leading tuned for readability and consistency
  fonts: {
    heading:
      'system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    body: 'system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  fontSizes: {
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
    '5xl': '48px',
  },
  lineHeights: {
    normal: '1.2',
    shorter: '1.25',
    short: '1.3',
    base: '1.5',
    tall: '1.6',
    taller: '1.8',
  },
  // Reusable surface styles for cards and overlays
  layerStyles: {
    card: {
      bg: 'bg.elevated',
      color: 'text.primary',
      borderWidth: '1px',
      borderColor: 'border.muted',
      borderRadius: 'lg',
      boxShadow: 'elevation.1',
    },
    overlayCard: {
      bg: 'bg.elevated',
      color: 'text.primary',
      borderWidth: '1px',
      borderColor: 'border.muted',
      borderRadius: 'lg',
      boxShadow: 'elevation.2',
    },
    glassCard: {
      backdropFilter: 'auto',
      backdropBlur: 'var(--tg-glass-blur)',
      bg: 'glass.bg',
      borderColor: 'glass.border',
      borderWidth: '1px',
      borderRadius: 'lg',
      boxShadow: 'glass',
    },
  },
  // Unified text styles for headings and body
  textStyles: {
    h1: { fontSize: '4xl', lineHeight: 'shorter', fontWeight: 700 },
    h2: { fontSize: '3xl', lineHeight: 'shorter', fontWeight: 700 },
    h3: { fontSize: '2xl', lineHeight: 'short', fontWeight: 700 },
    h4: { fontSize: 'xl', lineHeight: 'short', fontWeight: 700 },
    body: { fontSize: 'md', lineHeight: 'base' },
    caption: { fontSize: 'sm', lineHeight: 'short' },
  },
  shadows: {
    'elevation.1': 'var(--chakra-shadows-elevation-1)',
    'elevation.2': 'var(--chakra-shadows-elevation-2)',
    glass: 'var(--chakra-shadows-glass)',
  },
  components: {
    Button,
    Modal,
    Drawer,
    Popover,
    Tooltip,
    Menu,
    Toast,
    Input,
    Textarea,
    Select,
    Switch,
    Slider,
    Table,
  },
});

export type AppTheme = typeof theme;
